import * as Sentry from "@sentry/react";

import { getServiceName } from "./sentryUrlHelpers";

/**
 * Utility for filtering Sentry errors to reduce noise and focus on actionable errors
 * Following Sentry best practices for error filtering
 */

/**
 * Safely extracts error information from a Sentry event
 * @param event - Sentry error event
 * @returns Object containing extracted error information
 */
function extractErrorInfo(event: Sentry.ErrorEvent) {
  try {
    // Message events (Sentry.captureMessage) carry their text in event.message,
    // not event.exception — without the fallback they all read as "" and get
    // dropped by the generic-message filter below
    const errorMessage = event.exception?.values?.[0]?.value || event.message || "";
    const errorType = event.exception?.values?.[0]?.type || "";
    const errorFingerprint = event.fingerprint?.join(" ") || "";
    const errorUrl = event.request?.url || "";

    // Extract stack trace information if available
    const stackTrace =
      event.exception?.values?.[0]?.stacktrace?.frames
        ?.map((frame) => `${frame.filename}:${frame.function}`)
        .join(" ") || "";

    // Combine all error information for comprehensive filtering
    const combinedErrorInfo =
      `${errorMessage} ${errorType} ${errorFingerprint} ${errorUrl} ${stackTrace}`.toLowerCase();

    return {
      errorMessage,
      errorType,
      errorFingerprint,
      errorUrl,
      stackTrace,
      combinedErrorInfo
    };
  } catch (extractionError) {
    // If extraction fails, return empty strings to allow the error through
    // Better to have a false negative than to crash the error reporting
    // eslint-disable-next-line no-console
    console.warn("Sentry error info extraction failed:", extractionError);
    return {
      errorMessage: "",
      errorType: "",
      errorFingerprint: "",
      errorUrl: "",
      stackTrace: "",
      combinedErrorInfo: ""
    };
  }
}

/**
 * Checks if an error is from a third-party service that should be ignored
 * @param event - Sentry error event
 * @returns true if the error should be filtered out
 */
function shouldFilterThirdPartyService(event: Sentry.ErrorEvent): boolean {
  try {
    const errorUrl = event.request?.url || "";

    // Filter out Salesforce Web-to-Case submissions (CORS-opaque, fire-and-forget)
    if (errorUrl.includes("webto.salesforce.com")) {
      return true;
    }

    return false;
  } catch (thirdPartyFilterError) {
    // eslint-disable-next-line no-console
    console.warn("Sentry third-party service filtering failed:", thirdPartyFilterError);
    return false;
  }
}

/**
 * Gets the comprehensive list of error patterns to ignore
 * @returns Array of RegExp patterns for filtering errors
 */
function getIgnoredErrorPatterns(): RegExp[] {
  return [
    // Request cancellation and abortion (user-initiated, not app errors)
    /aborted?error/i,
    /abort/i,
    /canceled?/i,
    /cancelled/i,
    /request.?(was|has been).?aborted/i,
    /operation.?was.?aborted/i,
    /user.?aborted/i,
    /fetch.?aborted/i,

    // Network connectivity issues (infrastructure, not application errors)
    /networkerror/i,
    /network.?error/i,
    /failed.?to.?fetch/i,
    /load.?failed/i,
    /connection.?refused/i,
    /connection.?reset/i,
    /connection.?timeout/i,
    /timeout/i,
    /err_network/i,
    /err_internet_disconnected/i,
    /err_connection/i,
    /net::/i,

    // Browser extension interference (not app-related)
    /extension/i,
    /chrome-extension/i,
    /moz-extension/i,
    /safari-extension/i,
    /web-extension/i,

    // Generic script errors from external sources or cross-origin issues
    /script.?error/i,
    /script.?load.?error/i,
    /non-error.?promise.?rejection/i,
    /unhandled.?promise.?rejection/i,
    /promise.?rejection.?captured/i,
    /uncaught.?in.?promise/i,

    // Navigation and page lifecycle errors (expected behavior)
    /navigation.?was.?aborted/i,
    /navigation.?cancelled/i,
    /page.?unload/i,
    /beforeunload/i,
    /unload/i,

    // Quota and storage errors (user device limitations)
    /quota.?exceeded/i,
    /storage.?quota/i,
    /localstorage/i,
    /sessionstorage/i,

    // CORS and security errors (configuration, not app logic errors)
    /cors/i,
    /cross.?origin/i,
    /not.?allowed.?by.?access-control/i,

    // Ad blockers and security software interference
    /blocked.?by.?client/i,
    /ad.?block/i,
    /ublock/i,
    /privacy.?badger/i,

    // Development and debugging errors
    /development/i,
    /localhost/i,
    /devtools/i,

    // Common browser quirks and non-actionable errors
    /request.?failed.?with.?status.?code.?0/i,
    /status.?code.?0/i,
    /unexpected.?token/i, // Often from ad blockers or extensions modifying content
    /loading.?chunk.?\d+.?failed/i,
    /loading.?css.?chunk/i,

    // Specific to common libraries and expected behaviors
    /resizeobserver.?loop.?limit.?exceeded/i,
    /keydown.?event/i,
    /popstate/i,
    /hashchange/i
  ];
}

/**
 * Checks if an error should be filtered based on its source domain
 * @param errorUrl - URL where the error occurred
 * @param environment - Current Sentry environment
 * @returns true if the error should be filtered out
 */
function shouldFilterByDomain(errorUrl: string, environment: string): boolean {
  try {
    // Only filter by domain in production to avoid false positives in dev
    if (!errorUrl || environment !== "production") {
      return false;
    }

    const url = new URL(errorUrl);
    const currentDomain = window.location.hostname;

    // Ignore errors from external domains (CDNs, third-party scripts, etc.)
    if (url.hostname !== currentDomain && !url.hostname.includes(currentDomain)) {
      return true;
    }

    return false;
  } catch (domainFilterError) {
    // If URL parsing fails, don't filter the error
    // eslint-disable-next-line no-console
    console.warn("Sentry domain filtering failed:", domainFilterError);
    return false;
  }
}

/**
 * Checks if an error message is too generic or short to be useful
 * @param errorMessage - The error message to check
 * @returns true if the error should be filtered out
 */
function shouldFilterGenericMessage(errorMessage: string): boolean {
  try {
    // Filter out errors with very short or generic messages (often noise)
    if (errorMessage.length < 3) {
      return true;
    }

    const genericMessages = [
      "Script error.",
      "Error",
      "undefined",
      "null",
      "Cannot read property",
      "Cannot read properties"
    ];

    return genericMessages.some((generic) => errorMessage === generic);
  } catch (genericFilterError) {
    // eslint-disable-next-line no-console
    console.warn("Sentry generic message filtering failed:", genericFilterError);
    return false;
  }
}

/**
 * Main error filtering function that determines if a Sentry error should be ignored
 * Wrapped in try-catch to prevent the filter itself from causing errors
 *
 * @param event - Sentry error event
 * @returns true if the error should be filtered out (not sent to Sentry)
 */
export function shouldFilterSentryError(event: Sentry.ErrorEvent): boolean {
  try {
    // Check third-party services first (most specific check)
    if (shouldFilterThirdPartyService(event)) {
      return true;
    }

    // Extract error information safely
    const { errorMessage, errorUrl, combinedErrorInfo } = extractErrorInfo(event);

    // Get ignored patterns
    const ignoredPatterns = getIgnoredErrorPatterns();

    // Check against ignored patterns
    const matchesIgnoredPattern = ignoredPatterns.some((pattern) =>
      pattern.test(combinedErrorInfo)
    );

    if (matchesIgnoredPattern) {
      return true;
    }

    // Check domain-based filtering
    if (shouldFilterByDomain(errorUrl, event.environment || "development")) {
      return true;
    }

    // Check for generic messages
    if (shouldFilterGenericMessage(errorMessage)) {
      return true;
    }

    // If none of the filters apply, allow the error through
    return false;
  } catch (filterError) {
    // If the filter itself throws an error, log it but don't filter the original error
    // This prevents the filter from causing infinite loops or masking real errors
    // eslint-disable-next-line no-console
    console.warn("Sentry error filter encountered an error:", filterError);

    // Allow the original error through when the filter fails
    return false;
  }
}

/**
 * Enhanced error context builder for better debugging
 * Safely adds additional context to Sentry events
 *
 * @param event - Sentry error event
 * @param reactAppEnv - Current React app environment
 * @returns Enhanced event with additional context, or original event if enhancement fails
 */
export function enhanceSentryErrorContext(
  event: Sentry.ErrorEvent,
  reactAppEnv: string
): Sentry.ErrorEvent {
  try {
    // Get the error URL for service name extraction
    const errorUrl = event.request?.url || "";
    const serviceName = getServiceName(errorUrl);

    // Enhance error context with additional debugging information
    const enhancedEvent = {
      ...event,
      tags: {
        ...event.tags,
        deployment_env: reactAppEnv,
        hostname: window.location.hostname,
        user_agent: navigator.userAgent,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        screen_size: `${window.screen.width}x${window.screen.height}`,
        ...(serviceName && { service_name: serviceName })
      },
      contexts: {
        ...event.contexts,
        browser: {
          name: navigator.userAgent,
          version: navigator.appVersion,
          online: navigator.onLine
        },
        page: {
          url: window.location.href,
          referrer: document.referrer,
          title: document.title
        },
        ...(errorUrl &&
          serviceName && {
            api_context: {
              url: errorUrl,
              service_name: serviceName
            }
          })
      }
    };

    // Add custom fingerprinting for better error grouping
    if (event.exception?.values?.[0]) {
      const exception = event.exception.values[0];
      const errorKey = `${exception.type}-${exception.value?.substring(0, 100)}`;
      const fingerprint = [errorKey];

      // Include service name in fingerprint if available
      if (serviceName) {
        fingerprint.push(serviceName);
      }

      enhancedEvent.fingerprint = fingerprint;
    }

    return enhancedEvent;
  } catch (enhancementError) {
    // If enhancement fails, log the error but return the original event
    // eslint-disable-next-line no-console
    console.warn("Sentry error context enhancement failed:", enhancementError);
    return event;
  }
}
